import { Fragment, useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import { useToast } from '../ui/Toast';
import PortalAccessPanel from './PortalAccessPanel';
import ClientPortalSections from './ClientPortalSections';
import SectionPicker from './SectionPicker';

export default function ClientsPage() {
  const [clients, setClients]       = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [mergeClient, setMergeClient] = useState(null); // client to merge away
  const [expanded, setExpanded]     = useState(null);
  const { toast } = useToast();

  const load = () => api.get('clients').then(setClients);
  useEffect(() => { load(); }, []);

  const handleCreate = async ({ _portal_sections, ...data }) => {
    const created = await api.post('clients', data);
    // Applied after the client exists, since the sections hang off its id.
    // Failing here must not read as the client failing to save: it did.
    for (const key of _portal_sections || []) {
      await api.put(`client-portal-access/client/${created.id}/features`, { feature_key: key, enabled: true })
        .catch(() => toast.error(`Client saved, but ${key} could not be switched on.`));
    }
    setShowCreate(false);
    load();
  };

  const handleSave = async (id, data) => {
    await api.put(`clients/${id}`, data);
    setEditClient(null);
    load();
  };

  const handleDelete = async (c) => {
    if (!confirm(`Delete client "${c.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`clients/${c.id}`);
      load();
    } catch (e) {
      const msg = e.message || 'Failed to delete client';
      toast.error(msg);
    }
  };

  const handleMerge = async (sourceClient, targetId) => {
    try {
      await api.post('import/clients/bulk-delete', { ids: [sourceClient.id], merge_into: targetId });
      setMergeClient(null);
      toast.success(`"${sourceClient.name}" merged — all SOWs and contacts moved to the selected client.`);
      load();
    } catch (e) {
      toast.error(e.message || 'Merge failed');
    }
  };

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">Clients</h2>
          <div className="text-[9px] text-gray-500">{clients.length} clients</div>
        </div>
        <button onClick={() => setShowCreate(true)} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">
          + New Client
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className="px-2 py-2 text-left text-[8px] font-bold text-gray-500 uppercase border-b bg-gray-50 w-5"></th>
              <th className="px-3 py-2 text-left text-[8px] font-bold text-gray-500 uppercase border-b bg-gray-50">Name</th>
              <th className="px-3 py-2 text-left text-[8px] font-bold text-gray-500 uppercase border-b bg-gray-50">Code</th>
              <th className="px-3 py-2 text-left text-[8px] font-bold text-gray-500 uppercase border-b bg-gray-50">Primary Contact</th>
              <th className="px-3 py-2 text-left text-[8px] font-bold text-gray-500 uppercase border-b bg-gray-50">Email</th>
              <th className="px-3 py-2 text-left text-[8px] font-bold text-gray-500 uppercase border-b bg-gray-50">Address</th>
              <th className="px-3 py-2 text-center text-[8px] font-bold text-gray-500 uppercase border-b bg-gray-50">Pay Terms</th>
              <th className="px-3 py-2 text-right text-[8px] font-bold text-gray-500 uppercase border-b bg-gray-50">SOWs</th>
              <th className="px-3 py-2 text-center text-[8px] font-bold text-gray-500 uppercase border-b bg-gray-50">Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.map(c => (
              <Fragment key={c.id}>
                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-2 py-2 text-[9px] text-gray-400 cursor-pointer" onClick={() => setExpanded(expanded === c.id ? null : c.id)}>
                    {expanded === c.id ? '▾' : '▸'}
                  </td>
                  <td className="px-3 py-2 text-[10px] font-semibold">{c.name}</td>
                  <td className="px-3 py-2 text-[9px] text-gray-500">{c.code || '-'}</td>
                  <td className="px-3 py-2 text-[9px]">{c.primary_contact || '-'}</td>
                  <td className="px-3 py-2 text-[9px]">{c.contact_email || '-'}</td>
                  <td className="px-3 py-2 text-[9px] text-gray-500 max-w-[200px] truncate">{c.address || '-'}</td>
                  <td className="px-3 py-2 text-[9px] text-center">{c.payment_terms ? `${c.payment_terms}d` : '-'}</td>
                  <td className="px-3 py-2 text-[9px] text-right">
                    {c.sow_count > 0
                      ? <span className="font-semibold text-navy cursor-help" title={c.sow_refs || ''}>{c.sow_count}</span>
                      : <span className="text-gray-300">0</span>}
                  </td>
                  <td className="px-3 py-2 text-center whitespace-nowrap">
                    <button onClick={() => setEditClient(c)} className="text-[8px] text-gold underline mr-2">Edit</button>
                    <button onClick={() => setMergeClient(c)} className="text-[8px] text-blue-500 underline mr-2" title="Move all SOWs to another client then delete this one">Merge into…</button>
                    <button onClick={() => handleDelete(c)} className="text-[8px] text-red-400 underline">Delete</button>
                  </td>
                </tr>
                {expanded === c.id && (
                  <tr>
                    <td colSpan="9" className="px-0 py-0 bg-indigo-50/30">
                      <ContactsSection clientId={c.id} clientName={c.name} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {clients.length === 0 && (
              <tr><td colSpan="9" className="text-center py-8 text-[10px] text-gray-400">No clients yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <ClientModal title="New Client" onClose={() => setShowCreate(false)} onSave={handleCreate} />
      )}
      {editClient && (
        <ClientModal
          title={`Edit: ${editClient.name}`}
          client={editClient}
          onClose={() => setEditClient(null)}
          onSave={(data) => handleSave(editClient.id, data)}
        />
      )}
      {mergeClient && (
        <MergeClientModal
          client={mergeClient}
          allClients={clients}
          onClose={() => setMergeClient(null)}
          onMerge={(targetId) => handleMerge(mergeClient, targetId)}
        />
      )}
    </div>
  );
}

/* ─── Merge client modal ─────────────────────────────────────────────────── */

function MergeClientModal({ client, allClients, onClose, onMerge }) {
  const [targetId, setTargetId] = useState('');
  const others = allClients.filter(c => c.id !== client.id);
  const target = others.find(c => c.id === targetId);

  return (
    <Modal
      title={`Merge "${client.name}" into another client`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
          <button
            onClick={() => { if (targetId) onMerge(targetId); }}
            disabled={!targetId}
            className="px-3 py-1 text-[10px] font-semibold bg-red-600 text-white rounded disabled:opacity-40 hover:bg-red-700"
          >
            Merge &amp; Delete "{client.name}"
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="p-3 bg-amber-50 border border-amber-200 rounded text-[9px] text-amber-800">
          <div className="font-bold mb-0.5">This will permanently delete "{client.name}".</div>
          All {client.sow_count > 0 ? `${client.sow_count} SOW${client.sow_count !== 1 ? 's' : ''}` : 'SOWs'}, opportunities, projects and contacts linked to this client will be reassigned to the selected client.
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Move everything into *</label>
          <select
            className="form-select w-full"
            value={targetId}
            onChange={e => setTargetId(e.target.value)}
            autoFocus
          >
            <option value="">— select target client —</option>
            {others.map(c => (
              <option key={c.id} value={c.id}>{c.name}{c.sow_count ? ` (${c.sow_count} SOWs)` : ''}</option>
            ))}
          </select>
        </div>
        {target && (
          <div className="p-2 bg-blue-50 border border-blue-200 rounded text-[9px] text-blue-700">
            All data from <strong>"{client.name}"</strong> will be moved to <strong>"{target.name}"</strong>, then "{client.name}" will be deleted.
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ─── Contacts section (expandable per client) ───────────────────────────── */

function ContactsSection({ clientId, clientName }) {
  const [contacts, setContacts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]     = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [portalContact, setPortalContact] = useState(null);
  const { toast } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    api.get(`clients/${clientId}/contacts`)
      .then(data => { setContacts(data || []); setLoading(false); })
      .catch(() => { setContacts([]); setLoading(false); });
  }, [clientId]);

  useEffect(load, [load]);

  const handleDelete = async (contact) => {
    if (!confirm(`Delete contact "${contact.first_name} ${contact.last_name}"?`)) return;
    await api.delete(`client-contacts/${contact.id}`);
    load();
  };

  const handleSetPrimary = async (contact) => {
    await api.put(`client-contacts/${contact.id}`, { ...contact, is_primary: 1 });
    load();
  };

  return (
    <div className="px-8 py-3 border-t border-indigo-100">
      {/* The ceiling for this client, above the contacts it governs. */}
      <ClientPortalSections clientId={clientId} />
      <div className="flex items-center justify-between mb-2">
        <div className="text-[8px] font-bold text-indigo-700 uppercase tracking-wide">
          Contacts — {clientName}
          {!loading && <span className="ml-1 text-indigo-400 font-normal">({contacts.length})</span>}
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="px-2 py-0.5 bg-indigo-600 text-white text-[8px] font-semibold rounded hover:bg-indigo-700"
        >
          + Add Contact
        </button>
      </div>

      {loading ? (
        <div className="text-[9px] text-gray-400 py-2">Loading…</div>
      ) : contacts.length === 0 ? (
        <div className="text-[9px] text-gray-400 py-2">No contacts yet — add one above.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="pr-3 pb-1 text-left text-[7px] font-bold text-gray-400 uppercase">Name</th>
                <th className="pr-3 pb-1 text-left text-[7px] font-bold text-gray-400 uppercase">Role</th>
                <th className="pr-3 pb-1 text-left text-[7px] font-bold text-gray-400 uppercase">Email</th>
                <th className="pr-3 pb-1 text-left text-[7px] font-bold text-gray-400 uppercase">Phone</th>
                <th className="pr-3 pb-1 text-left text-[7px] font-bold text-gray-400 uppercase">Notes</th>
                <th className="pb-1 text-center text-[7px] font-bold text-gray-400 uppercase">Primary</th>
                <th className="pb-1 text-center text-[7px] font-bold text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(ct => (
                <Fragment key={ct.id}>
                <tr className="border-t border-indigo-100/60">
                  <td className="pr-3 py-1.5 text-[9px] font-semibold whitespace-nowrap">
                    {ct.first_name} {ct.last_name}
                    {!!Number(ct.is_non_commercial) && <span className="ml-1.5 text-[7px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded font-semibold">Non-commercial</span>}
                  </td>
                  <td className="pr-3 py-1.5 text-[9px] text-gray-500">{ct.role || '—'}</td>
                  <td className="pr-3 py-1.5 text-[9px]">
                    {ct.email
                      ? <a href={`mailto:${ct.email}`} className="text-blue-600 hover:underline">{ct.email}</a>
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="pr-3 py-1.5 text-[9px]">{ct.phone || <span className="text-gray-400">—</span>}</td>
                  <td className="pr-3 py-1.5 text-[9px] text-gray-500 max-w-[160px] truncate">{ct.notes || '—'}</td>
                  <td className="py-1.5 text-center">
                    {ct.is_primary
                      ? <span className="text-[7px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded font-semibold">Primary</span>
                      : <button onClick={() => handleSetPrimary(ct)} className="text-[7px] text-gray-400 hover:text-indigo-600 underline">Set</button>}
                  </td>
                  <td className="py-1.5 text-center whitespace-nowrap">
                    <button onClick={() => setPortalContact(portalContact === ct.id ? null : ct.id)}
                      className="text-[8px] text-navy hover:underline mr-1">Portal</button>
                    <button onClick={() => setEditContact(ct)} className="text-[8px] text-gold hover:underline mr-1">Edit</button>
                    <button onClick={() => handleDelete(ct)} className="text-[8px] text-red-400 hover:text-red-600">Delete</button>
                  </td>
                </tr>
                {portalContact === ct.id && (
                  <tr>
                    <td colSpan={7} className="pb-2">
                      <PortalAccessPanel contact={ct} onClose={() => setPortalContact(null)} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <ContactModal
          clientId={clientId}
          onClose={() => setShowAdd(false)}
          onSave={async ({ _portal_sections, ...data }) => {
            const created = await api.post('client-contacts', { ...data, client_id: clientId });
            for (const key of _portal_sections || []) {
              await api.put(`client-portal-access/${created.id}/features`, { feature_key: key, granted: true })
                .catch(() => toast.error(`Contact saved, but ${key} could not be switched on.`));
            }
            setShowAdd(false);
            load();
          }}
        />
      )}
      {editContact && (
        <ContactModal
          clientId={clientId}
          contact={editContact}
          onClose={() => setEditContact(null)}
          onSave={async (data) => { await api.put(`client-contacts/${editContact.id}`, data); setEditContact(null); load(); }}
        />
      )}
    </div>
  );
}

/* ─── Contact modal ───────────────────────────────────────────────────────── */

function ContactModal({ contact, clientId, onClose, onSave }) {
  const [form, setForm] = useState({
    first_name: contact?.first_name || '',
    last_name:  contact?.last_name  || '',
    email:      contact?.email      || '',
    phone:      contact?.phone      || '',
    role:       contact?.role       || '',
    notes:      contact?.notes      || '',
    is_primary: contact?.is_primary ?? 0,
    is_non_commercial: contact?.is_non_commercial ?? 0,
  });
  const [errors, setErrors] = useState({});
  const [sections, setSections] = useState([]);
  const [clientSections, setClientSections] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // What the client has switched on is the ceiling, so only those are offered.
  useEffect(() => {
    if (contact) return;             // existing contacts are managed in their Portal panel
    api.get(`client-portal-access/client/${clientId}`)
      .then(r => setClientSections(r.client.features.filter(f => f.enabled).map(f => f.key)))
      .catch(() => setClientSections([]));
  }, [clientId, contact]);

  const handleSave = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = true;
    if (!form.last_name.trim())  e.last_name  = true;
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...form, is_primary: form.is_primary ? 1 : 0, is_non_commercial: form.is_non_commercial ? 1 : 0, _portal_sections: contact ? undefined : sections });
  };

  return (
    <Modal
      title={contact ? `Edit Contact — ${contact.first_name} ${contact.last_name}` : 'New Client Contact'}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
          <button onClick={handleSave} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded">Save</button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">First Name *</label>
          <input
            className={`form-input ${errors.first_name ? 'border-red-400' : ''}`}
            value={form.first_name}
            onChange={e => { set('first_name', e.target.value); setErrors(p => ({ ...p, first_name: false })); }}
            autoFocus
          />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Last Name *</label>
          <input
            className={`form-input ${errors.last_name ? 'border-red-400' : ''}`}
            value={form.last_name}
            onChange={e => { set('last_name', e.target.value); setErrors(p => ({ ...p, last_name: false })); }}
          />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Email</label>
          <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Phone</label>
          <input className="form-input" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Role / Title</label>
          <input className="form-input" value={form.role} onChange={e => set('role', e.target.value)} placeholder="e.g. Project Manager" />
        </div>
        <div className="flex flex-col justify-end gap-1 pb-0.5">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.is_primary}
              onChange={e => set('is_primary', e.target.checked ? 1 : 0)}
              className="w-3 h-3 accent-indigo-600"
            />
            <span className="text-[9px] font-semibold text-gray-600">Primary contact</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer" title="Works with us on delivery but does not see prices. Offered as a SOW's non-commercial contact, not its commercial one.">
            <input
              type="checkbox"
              checked={!!Number(form.is_non_commercial)}
              onChange={e => set('is_non_commercial', e.target.checked ? 1 : 0)}
              className="w-3 h-3 accent-indigo-600"
            />
            <span className="text-[9px] font-semibold text-gray-600">Non-commercial <span className="font-normal text-gray-400">(does not see prices)</span></span>
          </label>
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes</label>
          <textarea className="form-textarea" rows="2" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any useful context about this contact…" />
        </div>
        {!contact && clientSections && (
          <SectionPicker
            label="Portal sections for this person"
            value={sections} onChange={setSections} availableKeys={clientSections}
            hint="They still need an invite before they can sign in. Only what this client has switched on is offered."
          />
        )}
      </div>
    </Modal>
  );
}

/* ─── Client modal ───────────────────────────────────────────────────────── */

function ClientModal({ title, client, onClose, onSave }) {
  const { toast } = useToast();
  // Only offered when creating. An existing client's sections are managed on
  // its own row, where they sit above the contacts they govern.
  const [sections, setSections] = useState([]);
  const [form, setForm] = useState({
    name:            client?.name            || '',
    code:            client?.code            || '',
    address:         client?.address         || '',
    primary_contact: client?.primary_contact || '',
    contact_email:   client?.contact_email   || '',
    payment_terms:   client?.payment_terms   ?? 30,
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <Modal title={title} onClose={onClose} footer={
      <>
        <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Cancel</button>
        <button
          onClick={() => {
            if (!form.name.trim()) { toast.error('Name is required'); return; }
            onSave({ ...form, payment_terms: parseInt(form.payment_terms) || 30, _portal_sections: client ? undefined : sections });
          }}
          className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded"
        >
          Save
        </button>
      </>
    }>
      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Name *</label>
          <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Code</label>
          <input className="form-input" value={form.code} onChange={e => set('code', e.target.value)} placeholder="e.g. CLIENT01" />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Payment Terms (days)</label>
          <input className="form-input" type="number" value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Primary Contact</label>
          <input className="form-input" value={form.primary_contact} onChange={e => set('primary_contact', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Contact Email</label>
          <input className="form-input" type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Address</label>
          <textarea className="form-textarea" rows="2" value={form.address} onChange={e => set('address', e.target.value)} />
        </div>
        {!client && (
          <SectionPicker
            label="Portal sections available to this client"
            value={sections} onChange={setSections}
            hint="This permits rather than grants: each contact is switched on separately afterwards."
          />
        )}
      </div>
    </Modal>
  );
}
